import { filterTypesKey, I_FilterItem, I_PriceRange, priceType } from '@Redux/search/interfaces';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';

interface I_UpdateFilterProps {
    data: I_FilterItem[];
    nowType: string;
    text: string;
    value: number | string;
    subValue: string;
    priceDefaultRange?: number[]; // 價錢初始最大最小值
}

/** @summary 篩選掉目前傳入的item
 * @param props I_UpdateFilterProps
 * @returns 剩下未被篩選掉的item
 */
export const updateFilterItems = (props: I_UpdateFilterProps) => {
    const { data, nowType, value, text, subValue, priceDefaultRange } = props;
    const newItems = JSON.parse(JSON.stringify(data));

    switch (subValue) {
        case priceType.min:
        case priceType.max: {
            const isMatchPrice = newItems.filter((item: I_FilterItem) => {
                const isSameSubValue = item.subValue === subValue;
                return isSameSubValue;
            });
            // 如果是相同min or max 則更新成新值
            newItems.forEach((item: I_FilterItem, idx: number, arr: []) => {
                const istSameType = item.filterBy === nowType;
                const isSameSubValue = item.subValue === subValue;
                if (istSameType && isSameSubValue) {
                    const initMin = priceDefaultRange && priceDefaultRange[0];
                    const initMax = priceDefaultRange && priceDefaultRange[1];
                    if (subValue === priceType.min && value === initMin) {
                        arr.splice(idx, 1);
                    } else if (subValue === priceType.max && value === initMax) {
                        arr.splice(idx, 1);
                    } else {
                        item.text = text;
                        item.value = value;
                    }
                }
            });

            if (isMatchPrice.length === 0) {
                // 無選到已有選項則新增一個
                newItems.push({
                    filterBy: nowType,
                    text,
                    value,
                    subValue,
                });
            }
            return newItems;
        }

        default: {
            // 回傳目前點到的項目是否已在目前篩選陣列中
            const isMatch = newItems.filter((item: I_FilterItem) => {
                return item.filterBy === nowType && item.value == value;
            });

            // 回傳所有 非相同類別 或 同類別但不同值
            const notMatchItem = newItems.filter((item: I_FilterItem) => {
                const istSameType = item.filterBy === nowType;
                const isSameValue = item.value == value;
                return !istSameType || (istSameType && !isSameValue);
            });
            if (isMatch.length === 0) {
                // 無選到已有選項則新增一個
                newItems.push({
                    filterBy: nowType,
                    text,
                    value,
                    subValue,
                });
                return newItems;
            } else {
                // 選到已有選項則回傳已被篩掉重複的選項陣列
                return notMatchItem;
            }
        }
    }
};

/** @summary 將condition裡面的filteritems轉換成可request使用的資料格式
 * @param params filterItems=> I_FilterItem[], initPriceRange=> 初始最大與最小價錢物件
 * @returns 可用於發request的資料格式物件
 */
export const transformConditionData = (params: {
    filterItems: I_FilterItem[];
    initPriceRange: I_PriceRange;
}) => {
    const { filterItems, initPriceRange } = params;

    const data: any = {
        [filterTypesKey.DepartureList]: [],
        [filterTypesKey.WeekDays]: [],
        [filterTypesKey.DayList]: [],
        [filterTypesKey.AirlineList]: [],
        [filterTypesKey.TravelType]: [],
        [filterTypesKey.CategoryList]: [],
        [filterTypesKey.PriceRange]: {
            PriceStart: initPriceRange.PriceStart, // 如無拖曳最小價錢則用初始最小價錢
            PriceEnd: initPriceRange.PriceEnd, // 如無拖曳最大價錢則用初始最大價錢
        },
    };

    filterItems.forEach((item) => {
        switch (item.filterBy) {
            case filterTypesKey.PriceRange:
                if (item.subValue === priceType.min) {
                    data[filterTypesKey.PriceRange].PriceStart = item.value;
                }
                if (item.subValue === priceType.max) {
                    data[filterTypesKey.PriceRange].PriceEnd = item.value;
                }
                break;
            default:
                data[item.filterBy].push(item.value);
                break;
        }
    });

    // 團體跟團體自由行都有選要return 0
    const returnTravelType = (() => {
        const arrLength = data[filterTypesKey.TravelType].length;
        if (arrLength === 0) return null;
        if (arrLength > 1) return 0;
        return data[filterTypesKey.TravelType][0];
    })();

    const transformedData = {
        DepartureID: data[filterTypesKey.DepartureList].join(','),
        WeekDay: data[filterTypesKey.WeekDays].join(','),
        Days: data[filterTypesKey.DayList].join(','),
        AirlineIDs: data[filterTypesKey.AirlineList].join(','),
        TravelType: returnTravelType,
        Tags: data[filterTypesKey.CategoryList].join(','),
        PriceList: [
            {
                PriceStart: data[filterTypesKey.PriceRange].PriceStart,
                PriceEnd: data[filterTypesKey.PriceRange].PriceEnd,
            },
        ],
    };
    return transformedData;
};

/** @summary 將價格數字每三位數加逗號
 * @param price 輸入需轉換的數字
 * @returns 數字string 1,000,000
 */
export const formatPrice = (price: number) => {
    return new Intl.NumberFormat().format(price);
};

/** @summary 將文案限制顯示上限字數
 * @param text 需判斷的文案
 * @param limitLength 設定上限字數量
 * @returns 超過設定數量則會擷取後在最後接上 "..."
 */
export const limitTextLength = (text: string, limitLength: number) => {
    if (text.length > limitLength) return `${text.slice(0, limitLength)}...`;
    return text;
};

const ReorganizationUrl = (getUrl: string, params: { [key: string]: string }) => {
    const paramsName: { [key: string]: string } = {
        normgroupid: 'NormGroupID',
        groupid: 'GroupID',
        tourid: 'TourID',
    };
    const tempUrl = decodeURIComponent(getUrl).replace(' ', '').replace('?', '').split('&');
    let tempParams: any = {};
    let url = '';

    tempUrl.length > 0 &&
        tempUrl[0] &&
        tempUrl.forEach(function (item) {
            const tempItem = item.split('=');
            let itemName = tempItem[0];
            if (paramsName[itemName.toLowerCase()]) {
                itemName = paramsName[itemName.toLowerCase()];
            }
            tempParams[itemName] = tempItem[1];
        });

    for (const param in params) {
        const key = param.toLowerCase();
        if (paramsName[key]) {
            params[paramsName[key]] = params[param];
        }
    }
    tempUrl.length > 0 ? (tempParams = { ...tempParams, ...params }) : (tempParams = { ...params });

    for (const key in tempParams) {
        if (key != 'IsPreview' && key != 'IsEnsureGroup') {
            url += (url != '' ? '&' : '') + key + '=' + tempParams[key];
        }
    }

    return url;
};

/** @summary 重組網址
 * @param NormGroupID 該團NormGroupID
 * @param GroupID 該團GroupID
 * @returns detail.html?NormGroupID=xxxxx&GroupID=xxxxxx
 */
interface I_ChangeDetailHistory {
    NormGroupID: string;
    GroupID: string;
    TourID?: string;
}

export const changeDetailHistory = ({ NormGroupID, GroupID, TourID }: I_ChangeDetailHistory) => {
    history.pushState(
        null,
        '',
        '?' +
            ReorganizationUrl(location.search, {
                NormGroupID: NormGroupID,
                GroupID: GroupID,
            })
    );
};

/** @summary 將XX:XX時間格式轉為XX小時XX分鐘格式
 * @param time 需判斷的時間
 * @returns 3小時30分鐘
 */
export const timeTransform = (time: string) => {
    const splitTime = time.split(':');
    const splitHour = splitTime[0] === '00' || splitTime[0] === '0' ? '' : splitTime[0];
    const splitMin = splitTime[1] === '00' || splitTime[1] === '0' ? '' : splitTime[1];
    const hour = splitHour ? splitHour + '小時' : '';
    const min = splitMin ? splitMin + '分鐘' : '';
    return hour + min;
};

/** @summary 利用日期算出是星期幾
 * @param date 2023/10/31 
 * @param fullDate 默認 true
 * @returns true -> 2023/10/31 (一)、false -> 10/31 (一)
 */
export const getWeekday = (date: string, fullDate: boolean = true) => {
    const weekDay = dayjs(date).locale('zh-tw').format('dd');
    return fullDate ? `${date} (${weekDay})` : `${dayjs(date).format('MM/DD')} (${weekDay})`;
}